import { useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import { Link } from '@tanstack/react-router'
import { ArrowRight, MapPin, Users, Waves, Wifi, Info } from 'lucide-react'
import { api } from '@/lib/api'
import { formatRoomPrice, localizedName } from '@/lib/format'
import { useAppStore } from '@/lib/store'
import { usePageTitle } from '@/lib/usePageTitle'
import { RoomDetailsModal } from '@/components/RoomDetailsModal'
import { RoomBookingModal } from '@/components/RoomBookingModal'
import type { LodgingRoomType, Store } from '@/shared/types'

interface PublicSettings {
  shop_name?: string
}

export function HomePage() {
  const lang = useAppStore((s) => s.lang)

  const [detailsRoom, setDetailsRoom] = useState<LodgingRoomType | null>(null)
  const [bookingRoom, setBookingRoom] = useState<LodgingRoomType | null>(null)

  const { data: settings } = useQuery<PublicSettings>({
    queryKey: ['public-settings'],
    queryFn: () => api.get<PublicSettings>('/settings'),
    staleTime: 300_000,
  })

  const { data: stores } = useQuery<Store[]>({
    queryKey: ['stores'],
    queryFn: () => api.get<Store[]>('/stores'),
    staleTime: 300_000,
  })
  const store = stores?.[0]

  const { data: roomTypes } = useQuery<LodgingRoomType[]>({
    queryKey: ['lodging-room-types', 1],
    queryFn: () => api.get<LodgingRoomType[]>('/lodging/room-types?store_id=1'),
  })

  const t = {
    eyebrow: lang === 'en' ? 'Guesthouse in Gurzuf, Crimea' : lang === 'ru' ? 'Гостевой дом в Гурзуфе, Крым' : 'Гостьовий будинок у Гурзуфі, Крим',
    primary: lang === 'en' ? 'Book a room' : lang === 'ru' ? 'Забронировать номер' : 'Забронювати номер',
    beach: lang === 'en' ? '300m to the beach' : lang === 'ru' ? '300 м до пляжа' : '300 м до пляжу',
    wifi: lang === 'en' ? 'Wi-Fi in every room' : lang === 'ru' ? 'Wi-Fi в каждом номере' : 'Wi-Fi у кожному номері',
    location: lang === 'en' ? 'Adalary rocks view' : lang === 'ru' ? 'Вид на скалы Адалары' : 'Вид на скелі Адалари',
    roomsTitle: lang === 'en' ? 'Our rooms' : lang === 'ru' ? 'Наши номера' : 'Наші номери',
    allRooms: lang === 'en' ? 'See all rooms' : lang === 'ru' ? 'Все номера' : 'Усі номери',
    fromPrice: lang === 'en' ? 'from' : lang === 'ru' ? 'от' : 'від',
    perNight: lang === 'en' ? '/ night' : lang === 'ru' ? '/ ночь' : '/ ніч',
    detailsBtn: lang === 'en' ? 'Details' : lang === 'ru' ? 'Характеристики' : 'Характеристики',
    contactTitle: lang === 'en' ? 'How to reach us' : lang === 'ru' ? 'Как нас найти' : 'Як нас знайти',
    mapTitle: lang === 'en' ? 'Hotel on the map' : lang === 'ru' ? 'Отель на карте' : 'Готель на мапі',
    address: lang === 'en' ? 'Gurzuf, Gurzufskoye Highway, 9B' : lang === 'ru' ? 'Гурзуф, Гурзуфское шоссе, 9Б' : 'Гурзуф, Гурзуфське шосе, 9Б',
    mapButton: lang === 'en' ? 'Show on map' : lang === 'ru' ? 'Показать на карте' : 'Показати на мапі',
  }

  const heroPhoto = roomTypes?.[0]?.photo_url
  const contacts = store ? localizedName(lang, store.contacts_uk ?? '', store.contacts_en ?? '') : ''
  const shopName = settings?.shop_name || (store ? localizedName(lang, store.name_uk, store.name_en) : '')

  usePageTitle(shopName)

  return (
    <div className="min-h-screen bg-slate-950 pb-24 text-slate-100">
      {/* Hero */}
      <div className="relative h-[60vh] min-h-[380px] w-full overflow-hidden">
        {heroPhoto ? (
          <img src={heroPhoto} alt={shopName} className="absolute inset-0 h-full w-full object-cover" />
        ) : (
          <div className="absolute inset-0 bg-slate-900" />
        )}
        <div className="absolute inset-0 bg-gradient-to-t from-slate-950 via-slate-950/60 to-slate-950/10" />
        <div className="relative z-10 flex h-full flex-col justify-end px-5 pb-8 md:px-10">
          <p className="flex items-center gap-1.5 text-xs font-semibold uppercase tracking-[0.2em] text-indigo-300">
            <MapPin className="h-3.5 w-3.5" />
            {t.eyebrow}
          </p>
          <h1 className="mt-3 max-w-2xl text-3xl font-bold leading-tight text-white md:text-5xl">
            {shopName}
          </h1>
          <div className="mt-6">
            <Link
              to="/catalog"
              className="inline-flex items-center gap-2 rounded-full bg-indigo-600 px-6 py-3 text-sm font-semibold text-white shadow-lg shadow-indigo-600/25 transition hover:bg-indigo-500"
            >
              {t.primary}
              <ArrowRight className="h-4 w-4" />
            </Link>
          </div>
        </div>
      </div>

      {/* Highlights */}
      <div className="mx-auto grid max-w-5xl grid-cols-1 gap-3 px-5 py-8 sm:grid-cols-3 md:px-10">
        {[
          { icon: Waves, label: t.beach },
          { icon: Wifi, label: t.wifi },
          { icon: MapPin, label: t.location },
        ].map(({ icon: Icon, label }) => (
          <div key={label} className="flex items-center gap-3 rounded-2xl border border-slate-800 bg-slate-900 p-4">
            <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-xl bg-indigo-500/10 text-indigo-400">
              <Icon className="h-4.5 w-4.5" />
            </span>
            <span className="text-sm font-medium text-slate-200">{label}</span>
          </div>
        ))}
      </div>

      {/* Rooms showcase */}
      <section className="mx-auto max-w-5xl px-5 py-4 md:px-10">
        <div className="mb-4 flex items-end justify-between gap-4">
          <h2 className="text-xl font-bold text-white">{t.roomsTitle}</h2>
          <Link to="/catalog" className="inline-flex items-center gap-1 text-sm font-semibold text-indigo-400">
            {t.allRooms}
            <ArrowRight className="h-3.5 w-3.5" />
          </Link>
        </div>
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
          {(roomTypes ?? []).map((rt) => {
            const name = localizedName(lang, rt.name_uk, rt.name_en, rt.name_ru)
            return (
              <div
                key={rt.id}
                onClick={() => setDetailsRoom(rt)}
                className="group cursor-pointer overflow-hidden rounded-2xl border border-slate-800 bg-slate-900 transition hover:border-indigo-500/40"
              >
                {rt.photo_url && (
                  <img
                    src={rt.photo_url}
                    alt={name}
                    className="h-48 w-full object-cover transition group-hover:scale-[1.02]"
                  />
                )}
                <div className="p-4">
                  <p className="font-semibold text-slate-100">{name}</p>
                  <p className="mt-1 flex items-center gap-1 text-xs text-slate-400">
                    <Users className="h-3.5 w-3.5" /> {rt.capacity}
                  </p>
                  <div className="mt-3 flex items-center justify-between">
                    <p className="font-bold text-indigo-400">
                      {t.fromPrice} {formatRoomPrice(rt.base_price_uah)} {t.perNight}
                    </p>
                    <span className="flex items-center gap-1 rounded-xl bg-slate-800 px-2.5 py-1 text-xs font-medium text-slate-300 hover:bg-slate-700">
                      <Info className="h-3.5 w-3.5 text-indigo-400" />
                      {t.detailsBtn}
                    </span>
                  </div>
                </div>
              </div>
            )
          })}
        </div>
      </section>

      {/* Map */}
      <section className="mx-auto max-w-5xl px-5 py-6 md:px-10">
        <div className="overflow-hidden rounded-2xl border border-slate-800 bg-slate-900">
          <div className="flex flex-col gap-3 p-5 sm:flex-row sm:items-center sm:justify-between">
            <div>
              <h2 className="text-xl font-bold text-white">{t.mapTitle}</h2>
              <p className="mt-1 flex items-center gap-1.5 text-sm text-slate-300">
                <MapPin className="h-4 w-4 text-indigo-400" />
                {t.address}
              </p>
            </div>
            <a
              href="https://yandex.com/maps/org/gnyozdyshko/239756200774/"
              target="_blank"
              rel="noreferrer"
              className="inline-flex items-center justify-center gap-2 rounded-full bg-indigo-600 px-4 py-2 text-sm font-semibold text-white transition hover:bg-indigo-500"
            >
              {t.mapButton}
              <ArrowRight className="h-4 w-4" />
            </a>
          </div>
          <iframe
            title={t.mapTitle}
            src="https://www.google.com/maps?q=%D0%93%D1%83%D1%80%D0%B7%D1%83%D1%84%2C%20%D0%93%D1%83%D1%80%D0%B7%D1%83%D1%84%D1%81%D0%BA%D0%BE%D0%B5%20%D1%88%D0%BE%D1%81%D1%81%D0%B5%2C%209%D0%91&output=embed"
            className="h-72 w-full border-0"
            loading="lazy"
            referrerPolicy="no-referrer-when-downgrade"
          />
        </div>
      </section>

      {/* Contacts */}
      {contacts && (
        <section className="mx-auto max-w-5xl px-5 py-8 md:px-10">
          <div className="rounded-2xl border border-slate-800 bg-slate-900 p-5">
            <h2 className="text-sm font-semibold uppercase tracking-wider text-slate-400">{t.contactTitle}</h2>
            <p className="mt-2 whitespace-pre-line text-sm leading-6 text-slate-300">{contacts}</p>
          </div>
        </section>
      )}

      {/* Modals */}
      {detailsRoom && (
        <RoomDetailsModal
          roomType={detailsRoom}
          onClose={() => setDetailsRoom(null)}
          onBook={() => {
            const rt = detailsRoom
            setDetailsRoom(null)
            setBookingRoom(rt)
          }}
        />
      )}

      {bookingRoom && (
        <RoomBookingModal
          roomType={bookingRoom}
          onClose={() => setBookingRoom(null)}
        />
      )}
    </div>
  )
}
